import logger from '../utils/logger';
import {
  DEMO_CUSTOMERS,
  DEMO_TREATMENTS_YAMADA,
  DEMO_TREATMENTS_SATO,
  INSIGHT_DEMO_CUSTOMERS,
  INSIGHT_TREATMENTS_YAMADA_HANAKO,
  INSIGHT_TREATMENTS_SUZUKI,
  INSIGHT_TREATMENTS_SATO_MISAKI,
  INSIGHT_TREATMENTS_DEV,
  DEMO_CUSTOMERS_EXTENDED,
  DEMO_SERVICES,
  DEMO_RESERVATIONS,
} from '../data/initialData';
import { TreatmentService } from './treatmentService';
import { ExtendedCustomer } from '../types/salonboard';
import { AccountDataService } from './accountDataService';

export class InitialDataService {
  private static readonly INITIAL_DATA_KEY = 'hasInitialData';

  static async setupInitialData(): Promise<void> {
    // 本番ではBE側シードデータに委任（デモデータをAPIに書き込まない）
    if (!import.meta.env.DEV) return;

    try {
      // ====================================
      // アカウント別初期データセットアップ
      // ====================================
      const accountId = AccountDataService.getCurrentAccountId();
      const accountEmail = AccountDataService.getCurrentAccountEmail();

      if (!accountId) {
        logger.warn(
          '⚠️ [InitialDataService] アカウントIDが設定されていません。初期データセットアップをスキップします。'
        );
        return;
      }

      logger.log(
        `📋 [InitialDataService] 初期データセットアップ開始: ${accountEmail} (ID: ${accountId})`
      );

      // 既にセットアップ済みかチェック
      if (AccountDataService.hasInitialData(accountId)) {
        logger.log(
          '✅ [InitialDataService] 初期データは既にセットアップ済みです'
        );
        return;
      }

      // ====================================
      // test0513アカウントの場合: デモデータをセットアップ
      // ====================================
      if (AccountDataService.isTestAccount(accountId)) {
        logger.log(
          '🎯 [InitialDataService] テストアカウント検出 → デモデータをセットアップします'
        );

        // デモ顧客データをセット（既存 + 追加6名）
        const demoCustomers = [
          ...DEMO_CUSTOMERS,
          ...INSIGHT_DEMO_CUSTOMERS,
          ...DEMO_CUSTOMERS_EXTENDED,
        ];
        AccountDataService.setCustomers(demoCustomers, accountId);
        logger.log(`👥 デモ顧客データ追加: ${demoCustomers.length}件`);

        // サービスメニューマスタデータをセット
        AccountDataService.setAccountData('services', DEMO_SERVICES, accountId);
        logger.log(`🛎️ デモサービスメニュー追加: ${DEMO_SERVICES.length}件`);

        // 予約データをセット
        AccountDataService.setAccountData(
          'reservations',
          DEMO_RESERVATIONS,
          accountId
        );
        logger.log(`📅 デモ予約データ追加: ${DEMO_RESERVATIONS.length}件`);

        // 山田花子さんの施術履歴
        logger.log('Setting up Yamada Hanako treatments...');
        for (const treatment of DEMO_TREATMENTS_YAMADA) {
          await TreatmentService.saveTreatmentRecord('1001', treatment);
        }
        logger.log(`✅ Yamada Hanako: ${DEMO_TREATMENTS_YAMADA.length}件`);

        // 佐藤太郎さんの施術履歴
        logger.log('Setting up Sato Taro treatments...');
        for (const treatment of DEMO_TREATMENTS_SATO) {
          await TreatmentService.saveTreatmentRecord('1002', treatment);
        }
        logger.log(`✅ Sato Taro: ${DEMO_TREATMENTS_SATO.length}件`);

        // インサイト用追加データ
        await this.setupInsightDemoDataForAccount(accountId);

        logger.log('🎉 テストアカウント用デモデータセットアップ完了');
      } else {
        // ====================================
        // その他のアカウント: クリーンな環境
        // ====================================
        logger.log(
          '🆕 [InitialDataService] 新規アカウント → クリーンな環境を提供'
        );

        // 空の顧客リストをセット
        AccountDataService.setCustomers([], accountId);
        logger.log('✅ 新規アカウント: クリーンな環境をセットアップしました');
      }

      // セットアップ完了フラグを設定
      AccountDataService.setInitialDataFlag(true, accountId);
      logger.log('✅ [InitialDataService] 初期データセットアップ完了');
    } catch (error) {
      logger.error(
        '❌ [InitialDataService] 初期データセットアップエラー:',
        error
      );
      // エラーが発生してもアプリケーションの起動は継続する
    }
  }

  /**
   * アカウント別インサイト用デモデータセットアップ
   */
  private static async setupInsightDemoDataForAccount(
    _accountId: string
  ): Promise<void> {
    try {
      logger.log(
        '📊 [InitialDataService] インサイト用デモデータをセットアップ中...'
      );

      // バッチ処理による施術記録のセットアップ
      const treatmentBatches = [
        {
          customerId: '1',
          customerName: 'Yamada Hanako',
          treatments: INSIGHT_TREATMENTS_YAMADA_HANAKO.slice(0, 5),
        },
        {
          customerId: '2',
          customerName: 'Suzuki Taro',
          treatments: INSIGHT_TREATMENTS_SUZUKI.slice(0, 5),
        },
        {
          customerId: '3',
          customerName: 'Sato Misaki',
          treatments: INSIGHT_TREATMENTS_SATO_MISAKI.slice(0, 3),
        },
        {
          customerId: '99',
          customerName: 'Dev Test',
          treatments: INSIGHT_TREATMENTS_DEV.slice(0, 5),
        },
      ];

      // 並列バッチ処理で施術記録を一括セットアップ
      await Promise.all(
        treatmentBatches.map(async (batch) => {
          logger.log(
            `Setting up ${batch.customerName} (ID:${batch.customerId}) treatments...`
          );

          // 顧客ごとの施術記録を一括処理
          const treatmentPromises = batch.treatments.map((treatment) =>
            TreatmentService.saveTreatmentRecord(batch.customerId, treatment)
          );

          await Promise.all(treatmentPromises);
          logger.log(`✅ ${batch.customerName}: ${batch.treatments.length}件`);
        })
      );

      logger.log(
        '✅ [InitialDataService] インサイト用デモデータセットアップ完了'
      );
    } catch (error) {
      logger.error(
        '❌ [InitialDataService] インサイトデモデータセットアップエラー:',
        error
      );
    }
  }

  // データ整合性をチェックする新しいメソッド
  static async checkDataIntegrity(): Promise<{
    isComplete: boolean;
    hasCustomers: boolean;
    hasYamadaTreatments: boolean;
    hasSatoTreatments: boolean;
    yamadaTreatmentCount: number;
    satoTreatmentCount: number;
  }> {
    try {
      // 顧客データの確認
      const customers = JSON.parse(localStorage.getItem('customers') || '[]');
      const hasYamada = customers.some(
        (c: ExtendedCustomer) => c.id === '1001'
      );
      const hasSato = customers.some((c: ExtendedCustomer) => c.id === '1002');
      const hasCustomers = hasYamada && hasSato;

      // 施術データの確認
      const yamadaTreatments =
        await TreatmentService.getTreatmentRecords('1001');
      const satoTreatments = await TreatmentService.getTreatmentRecords('1002');
      const hasYamadaTreatments = yamadaTreatments.length > 0;
      const hasSatoTreatments = satoTreatments.length > 0;

      const isComplete =
        hasCustomers && hasYamadaTreatments && hasSatoTreatments;

      return {
        isComplete,
        hasCustomers,
        hasYamadaTreatments,
        hasSatoTreatments,
        yamadaTreatmentCount: yamadaTreatments.length,
        satoTreatmentCount: satoTreatments.length,
      };
    } catch (error) {
      logger.error('Error checking data integrity:', error);
      return {
        isComplete: false,
        hasCustomers: false,
        hasYamadaTreatments: false,
        hasSatoTreatments: false,
        yamadaTreatmentCount: 0,
        satoTreatmentCount: 0,
      };
    }
  }

  // 顧客インサイト用デモデータをセットアップする新しいメソッド
  static async setupInsightDemoData(): Promise<void> {
    if (!import.meta.env.DEV) return;

    try {
      logger.log('Setting up customer insight demo data...');

      // 既存の顧客データを取得
      const existingCustomers = JSON.parse(
        localStorage.getItem('customers') || '[]'
      );

      // インサイト用顧客の重複チェックと追加
      const existingIds = existingCustomers.map((c: ExtendedCustomer) => c.id);

      const newInsightCustomers = INSIGHT_DEMO_CUSTOMERS.filter(
        (customer) => !existingIds.includes(customer.id)
      );

      if (newInsightCustomers.length > 0) {
        const updatedCustomers = [...existingCustomers, ...newInsightCustomers];
        localStorage.setItem('customers', JSON.stringify(updatedCustomers));
        logger.log('Insight demo customers added:', newInsightCustomers.length);
      }

      // バッチ処理による施術記録のセットアップ（性能大幅向上）
      const treatmentBatches = [
        {
          customerId: '1',
          customerName: 'Yamada Hanako',
          treatments: INSIGHT_TREATMENTS_YAMADA_HANAKO.slice(0, 5),
          currentCount: async () =>
            (await TreatmentService.getTreatmentRecords('1')).length,
        },
        {
          customerId: '2',
          customerName: 'Suzuki Taro',
          treatments: INSIGHT_TREATMENTS_SUZUKI.slice(0, 5),
          currentCount: async () =>
            (await TreatmentService.getTreatmentRecords('2')).length,
        },
        {
          customerId: '3',
          customerName: 'Sato Misaki',
          treatments: INSIGHT_TREATMENTS_SATO_MISAKI.slice(0, 3),
          currentCount: async () =>
            (await TreatmentService.getTreatmentRecords('3')).length,
        },
        {
          customerId: '99',
          customerName: 'Dev Test',
          treatments: INSIGHT_TREATMENTS_DEV.slice(0, 5),
          currentCount: async () =>
            (await TreatmentService.getTreatmentRecords('99')).length,
        },
      ];

      // 並列バッチ処理で施術記録を一括セットアップ
      await Promise.all(
        treatmentBatches.map(async (batch) => {
          if ((await batch.currentCount()) === 0) {
            logger.log(
              `Setting up ${batch.customerName} (ID:${batch.customerId}) treatments...`
            );

            // 顧客ごとの施術記録を一括処理
            const treatmentPromises = batch.treatments.map((treatment) =>
              TreatmentService.saveTreatmentRecord(batch.customerId, treatment)
            );

            await Promise.all(treatmentPromises);
            logger.log(
              `${batch.customerName} treatments added:`,
              batch.treatments.length
            );
          }
        })
      );

      logger.log('Customer insight demo data setup completed');

      // データ確認用のログ（非同期で実行し、メイン処理をブロックしない）
      setTimeout(async () => {
        const checkResults = {
          yamadaHanako: (await TreatmentService.getTreatmentRecords('1'))
            .length,
          suzukiTaro: (await TreatmentService.getTreatmentRecords('2')).length,
          satoMisaki: (await TreatmentService.getTreatmentRecords('3')).length,
          devTest: (await TreatmentService.getTreatmentRecords('99')).length,
        };
        logger.log('Treatment records count:', checkResults);
      }, 100);
    } catch (error) {
      logger.error('Failed to set up insight demo data:', error);
      // エラーが発生しても継続
    }
  }

  // 強制的にデモデータをセットアップする新しいメソッド（開発用）
  static async forceSetupDemoData(): Promise<void> {
    if (!import.meta.env.DEV) {
      logger.warn('forceSetupDemoData is only available in development mode');
      return;
    }

    try {
      // フラグをリセット
      localStorage.removeItem(this.INITIAL_DATA_KEY);

      // 既存のデモデータをクリア
      const customers = JSON.parse(localStorage.getItem('customers') || '[]');
      const filteredCustomers = customers.filter(
        (c: ExtendedCustomer) => c.id !== '1001' && c.id !== '1002'
      );
      localStorage.setItem('customers', JSON.stringify(filteredCustomers));

      // 施術データをクリア
      localStorage.removeItem('treatments_1001');
      localStorage.removeItem('treatments_1002');

      logger.log('Demo data cleared, re-running setup...');

      // 再セットアップ
      await this.setupInitialData();
    } catch (error) {
      logger.error('Failed to force setup demo data:', error);
    }
  }

  // デモデータをリセットする機能（開発用）
  static resetDemoData(): void {
    try {
      // セットアップフラグを削除
      localStorage.removeItem(this.INITIAL_DATA_KEY);

      // 顧客データからデモデータを削除
      const customers = JSON.parse(localStorage.getItem('customers') || '[]');
      // Filter out demo customers (ID range 1000-1999 as strings)
      const filteredCustomers = customers.filter((c: ExtendedCustomer) => {
        const numId = parseInt(String(c.id), 10);
        return isNaN(numId) || numId < 1000 || numId > 1999;
      });
      localStorage.setItem('customers', JSON.stringify(filteredCustomers));

      // 施術データを削除
      localStorage.removeItem('treatments_1001');
      localStorage.removeItem('treatments_1002');

      logger.log('Demo data reset successfully');
    } catch (error) {
      logger.error('Failed to reset demo data:', error);
    }
  }

  // デモデータが存在するかチェック
  static hasDemoData(): boolean {
    try {
      const customers = JSON.parse(localStorage.getItem('customers') || '[]');
      // Check for demo customers (ID 1001 or 1002)
      return customers.some(
        (c: ExtendedCustomer) => c.id === '1001' || c.id === '1002'
      );
    } catch {
      return false;
    }
  }
}
